import React, { useCallback, useEffect, useState } from 'react';

import { AdminBuyersView } from './AdminBuyers.view';

/**
 * Buyers, the container.
 *
 * ============================================================================
 * THIS ROUTE HAS NO BACKEND. READ BEFORE CHANGING ANYTHING HERE.
 * ============================================================================
 * `PLACEHOLDER_BUYERS` below is the app's original `mockBuyers` array. There is
 * no endpoint that lists buyers anywhere in `src/api`:
 *
 *   admin.module.js   admin login, dashboard stats, users list, activate and
 *                     deactivate a user, system settings. No buyers.
 *   seller.module.js  seller profiles and seller writes. No buyers.
 *   endpoints.js      no buyers path.
 *
 * NO ENDPOINT HAS BEEN INVENTED. The fetch below is deliberately still the
 * original fake: a timeout, then the fixture. What changed is that the page no
 * longer presents it as live data. `placeholderData` is passed to the view,
 * which renders a notice above the table saying so in words.
 *
 * WHEN THE REAL ENDPOINT LANDS:
 *   1. Replace the body of `fetchBuyers` with the call.
 *   2. Delete `PLACEHOLDER_BUYERS` and the `placeholderData` prop.
 *   3. Delete the `Notice` branch in `AdminBuyers.view.jsx`.
 * Nothing else on this screen has to change; the table is already the shared
 * one and the columns already describe the real record.
 * ============================================================================
 */

const IS_PLACEHOLDER_DATA = true;

/* The app's original `mockBuyers`, unchanged in shape. `verified` and `pending`
   are both real values in `utils/orderStatus.js`, so the status pill is honest
   about the tone even though the rows are not real. */
const PLACEHOLDER_BUYERS = [
  { id: 1, name: 'Alice Buyer', email: 'alice@gmail.com', status: 'verified', registered: '20/02/2024' },
  { id: 2, name: 'Bob Johnson', email: 'bob@gmail.com', status: 'pending', registered: '18/02/2024' },
  { id: 3, name: 'Carol Smith', email: 'carol@yahoo.com', status: 'verified', registered: '15/02/2024' },
];

const AdminBuyers = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBuyers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // NO REAL CALL EXISTS. See the block comment at the top of this file.
      // This is the original placeholder path, kept so the data flow stays in
      // one piece for whoever wires the endpoint.
      await new Promise((resolve) => setTimeout(resolve, 600));
      setBuyers(PLACEHOLDER_BUYERS);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuyers();
  }, [fetchBuyers]);

  return (
    <AdminBuyersView
      buyers={buyers}
      loading={loading}
      error={error}
      onRetry={fetchBuyers}
      placeholderData={IS_PLACEHOLDER_DATA && !loading && !error}
    />
  );
};

export default AdminBuyers;
